import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import ApWordmark from './platform/ApWordmark';
import { isSupabaseConfigured, supabase, supabaseAnonKey, supabaseUrl } from '../lib/supabase';
import {
  prepareOAuthRedirect,
  setIntendedRoute,
  setPrePaymentMarkers,
} from '../utils/authFlow';
import { DEFAULT_STUDY_ROUTE } from '../lib/studyNavigation';
import { hasActivePaidSubscription } from '../utils/accessState';
import {
  logApBioOnboardingEvent,
} from '../lib/apBioOnboarding';
import './platform/platform.css';
import './membership.css';

const normalizeReturnPath = (value) => {
  const text = typeof value === 'string' ? value.trim() : '';
  if (!text) return null;

  try {
    if (text.startsWith('http://') || text.startsWith('https://')) {
      return new URL(text).pathname || null;
    }
  } catch (error) {
    // ignore
  }

  if (!text.startsWith('/')) return null;
  return text;
};

const resolveReturnPath = ({ fromPath }) => {
  const candidate =
    normalizeReturnPath(fromPath) ||
    normalizeReturnPath(localStorage.getItem('intendedRoute')) ||
    DEFAULT_STUDY_ROUTE;

  const disallowed = new Set([
    '/membership',
    '/signup',
    '/signin',
    '/payment-success',
    '/auth/callback',
    '/onboarding',
  ]);

  if (!candidate || disallowed.has(candidate)) return DEFAULT_STUDY_ROUTE;
  return candidate;
};

const PlanCard = ({
  badge,
  title,
  priceLine,
  period,
  helper,
  trialLine,
  features,
  onSelect,
  buttonLabel,
  buttonHint,
  isLoading,
  disabled,
  isPopular,
  showFreeToday,
}) => {
  return (
    <article className="ap-membership-plan" data-highlighted={isPopular ? 'true' : 'false'}>
      {badge ? <p className="ap-membership-plan-badge">{badge.replace('🔥 ', '')}</p> : null}
      <h3>APDoneRight {title}</h3>
      <p className="ap-membership-plan-description">{helper}</p>
      <div className="ap-membership-price-block">
        <span className="ap-membership-card-rule" aria-hidden="true" />
        {showFreeToday ? (
          <>
            <p className="ap-membership-price"><s>{priceLine}</s><strong>$0</strong></p>
            <p className="ap-membership-price-note">today · then {priceLine}{period} after 3 days</p>
          </>
        ) : (
          <>
            <p className="ap-membership-price"><strong>{priceLine}</strong></p>
            <p className="ap-membership-price-note">{period.replace(/^\//, 'per ')}</p>
          </>
        )}
        {trialLine && !showFreeToday ? <p className="ap-membership-price-note">{trialLine}</p> : null}
      </div>

      <ul className="ap-membership-features">
        {features.map((text) => <li key={text}><Check aria-hidden="true" /><span>{text}</span></li>)}
      </ul>

      <div className="ap-membership-plan-action">
        <button
          type="button"
          disabled={disabled || isLoading}
          onClick={disabled || isLoading ? undefined : onSelect}
          className="ap-membership-checkout"
        >
          {isLoading ? (
            <span>Redirecting…</span>
          ) : (
            <span>{buttonLabel}</span>
          )}
        </button>
        {buttonHint ? (
          <p className="ap-membership-button-hint">{buttonHint}</p>
        ) : null}
      </div>
    </article>
  );
};


const MembershipSelection = ({ fromPath = null, message: _message = '', onboardingContext: entryOnboardingContext = null }) => {
  const navigate = useNavigate();
  const {
    user,
    profile,
    loading,
    signInWithGoogle,
    signOut,
    accessState,
    fetchUserSubscription,
  } = useAuth();

  const [billing, setBilling] = useState('monthly');
  const [loadingPlanId, setLoadingPlanId] = useState(null);
  const [error, setError] = useState('');
  const autoRedirectRef = useRef(false);

  useEffect(() => {
    // Arriving from onboarding: keep the personalised headline in view.
    if (entryOnboardingContext) return;
    const el = document.getElementById('plans-anchor');
    if (el) {
      el.scrollIntoView({ behavior: 'instant', block: 'start' });
      window.scrollBy({ top: -60, behavior: 'instant' });
    }
  }, []);

  const resolvedOnboardingContext = useMemo(() => (
    entryOnboardingContext || profile?.onboarding_context || null
  ), [entryOnboardingContext, profile?.onboarding_context]);

  const returnPath = useMemo(
    () => resolveReturnPath({ fromPath }),
    [fromPath]
  );

  const isReturningCustomer = Boolean(profile?.stripe_customer_id);
  const showBlockingSpinner = Boolean(!user && loading);

  // Show $0.00 prominently for new users
  const showFreeToday = !isReturningCustomer;
  const membershipCopy = useMemo(() => {
    const selectedCourseCount = Array.isArray(resolvedOnboardingContext?.selected_courses)
      ? resolvedOnboardingContext.selected_courses.length
      : 0;
    const firstName = String(resolvedOnboardingContext?.display_name || '').trim().split(/\s+/)[0];
    const targetScore = resolvedOnboardingContext?.target_score;
    const genericTitle = selectedCourseCount > 1
      ? `Unlock the study system for all ${selectedCourseCount} of your AP courses.`
      : 'Unlock your AP study system.';
    return {
      eyebrow: 'AP workspace access',
      title: firstName && /^[345]$/.test(targetScore || '') ? `${firstName}, start working toward your ${targetScore} today.` : genericTitle,
      subtitle: 'Use the same course-aware Tutor, flashcards, and daily planning flow across every AP subject you selected.',
      bullets: [
        'Each AP course keeps its own official content, Tutor context, and saved work.',
        'Move from a difficult course point into explanation, practice, and flashcards without losing your place.',
        'Cancel at any time from your account settings.',
      ],
    };
  }, [resolvedOnboardingContext]);

  useEffect(() => {
    if (!user || !accessState?.hasPaidAccess || autoRedirectRef.current) return;
    autoRedirectRef.current = true;
    navigate(returnPath || '/', { replace: true });
  }, [accessState?.hasPaidAccess, navigate, returnPath, user]);

  const plans = useMemo(() => {
    const isAnnual = billing === 'annual';
    const proId = isAnnual ? 'pro-annual' : 'pro';

    const allPlans = [
      {
        id: 'basic',
        title: 'Basic',
        priceLine: '$7.99',
        originalPrice: '$7.99',
        period: '/mo',
        helper: 'Structure & routine.',
        badge: null,
        isPopular: false,
        features: [
          '100 AI Tutor prompts each month',
          'Course-aware explanations for every selected AP subject',
          'AP-style question and answer coaching',
          'Flashcard generation — up to 20 cards per session',
          'Revision tracker and daily study planning',
        ],
      },
      {
        id: proId,
        title: 'Pro',
        priceLine: isAnnual ? '$5.00' : '$9.99',
        originalPrice: isAnnual ? '$10.00' : '$9.99',
        period: isAnnual ? '/mo, billed yearly' : '/mo',
        helper: isAnnual
          ? 'Save 50% with annual billing.'
          : 'Most popular. Fast improvement.',
        badge: '🔥 Most Popular',
        isPopular: true,
        features: [
          'Unlimited AI Tutor prompts',
          'Detailed AP answer feedback and scoring guidance',
          'Course-aware explanations for every selected AP subject',
          'Unlimited flashcard generation',
          'Revision tracker and daily study planning',
        ],
      },
      {
        id: 'pro-ultra',
        title: 'Pro Ultra',
        priceLine: '$14.99',
        originalPrice: '$14.99',
        period: '/mo',
        helper: 'VIP support & early access.',
        badge: 'limited',
        isPopular: false,
        features: [
          'Everything in Pro',
          'Flashcard Generator — up to 120 cards/session',
          'Priority support — faster response times',
          'Early access to new features',
        ],
      },
    ];

    if (isAnnual) {
      return allPlans.filter((p) => p.id === proId);
    }

    return allPlans;
  }, [billing]);

  const startGoogleSignIn = async () => {
    setError('');
    setIntendedRoute('/membership');

    const authPrep = await prepareOAuthRedirect({
      intendedRoute: '/membership',
      checkSession: async () => {
        const { data: sessionCheck } = await supabase.auth.getSession();
        return !!sessionCheck?.session;
      },
    });

    if (!authPrep.ok) {
      setError('Authentication in progress. Please wait.');
      return;
    }

    await signInWithGoogle({ forceAccountSelection: true });
  };

  const handleSignOut = async () => {
    setError('');
    try {
      await signOut();
      navigate('/signin?switch_account=1', { replace: true });
    } catch (signOutError) {
      setError(signOutError?.message || 'Could not sign out. Please try again.');
    }
  };

  const startCheckout = async (planId) => {
    setError('');
    setLoadingPlanId(planId);

    if (!user) {
      await startGoogleSignIn();
      setLoadingPlanId(null);
      return;
    }

    if (accessState?.hasPaidAccess) {
      setLoadingPlanId(null);
      navigate(returnPath || '/', { replace: true });
      return;
    }

    if (user?.id && typeof fetchUserSubscription === 'function') {
      try {
        const latestSubscription = await fetchUserSubscription(user.id, {
          updateState: true,
          markLoading: false,
        });

        if (hasActivePaidSubscription(latestSubscription)) {
          setLoadingPlanId(null);
          navigate(returnPath || '/', { replace: true });
          return;
        }
      } catch (subscriptionCheckError) {
        console.warn('[MembershipSelection] Subscription re-check failed before checkout:', subscriptionCheckError);
      }
    }

    try {
      if (user?.id) {
        void logApBioOnboardingEvent({
          userId: user.id,
          eventName: 'membership_cta_clicked',
          step: 3,
          metadata: {
            planId,
            returnPath,
            onboardingContext: resolvedOnboardingContext,
          },
        });
      }

      const { data: sessionData, error: sessionError } =
        await supabase.auth.getSession();

      if (sessionError || !sessionData.session) {
        throw new Error(sessionError?.message || 'Could not get user session.');
      }

      const checkoutFunctionUrl = supabaseUrl
        ? `${supabaseUrl}/functions/v1/create-checkout-session`
        : '';

      const storeReturnPath = () => {
        sessionStorage.setItem('pre-payment-location', returnPath);
        try {
          localStorage.removeItem('intendedRoute');
        } catch (error) {
          // non-blocking
        }
      };

      // Helper to try invoke or fetch
      const performCheckout = async (token) => {
        const headers = {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        };

        // Try supabase function invoke first if possible, otherwise fetch
        try {
          const { data: functionData, error: functionError } =
            await supabase.functions.invoke('create-checkout-session', {
              body: { planId },
            });
          if (functionError) throw functionError;
          return functionData;
        } catch (e) {
          if (!isSupabaseConfigured || !checkoutFunctionUrl || !supabaseAnonKey) {
            throw new Error('Supabase billing is not configured yet. Add VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY.');
          }

          // Fallback to direct fetch if invoke fails or we want consistent error handling
          const res = await fetch(checkoutFunctionUrl, {
            method: 'POST',
            headers: {
              ...headers,
              apikey: supabaseAnonKey
            },
            body: JSON.stringify({ planId })
          });
          const d = await res.json();
          if (!res.ok) throw new Error(d?.error || 'Checkout failed');
          return d;
        }
      }

      try {
        const accessToken = sessionData.session.access_token;
        const data = await performCheckout(accessToken);

        if (!data?.sessionUrl) {
          throw new Error('Invalid session URL from server.');
        }

        setPrePaymentMarkers({ session: sessionData.session, planId });
        storeReturnPath();
        window.location.href = data.sessionUrl;

      } catch (err) {
        throw err;
      }

    } catch (err) {
      setError(err?.message || 'Something went wrong. Please try again.');
      setLoadingPlanId(null);
    }
  };

  const primaryButtonLabel = isReturningCustomer
    ? 'Subscribe now'
    : 'Start Free → $0.00 today';
  const trialHint = isReturningCustomer
    ? 'No trial for returning users'
    : 'Cancel anytime • No charge today';
  const guestHint = isReturningCustomer
    ? 'Sign in to subscribe'
    : 'Sign in to start free';

  if (showBlockingSpinner) {
    return (
      <div className="ap-membership-page">
        <div className="ap-membership-loading" role="status">Preparing secure plan options…</div>
      </div>
    );
  }

  if (user && accessState?.hasPaidAccess) {
    return (
      <div className="ap-membership-page">
        <div className="ap-membership-shell is-active-plan">
          <header className="ap-membership-header"><ApWordmark /><button type="button" onClick={handleSignOut}>Switch account</button></header>
          <section className="ap-membership-active-card">
            <p className="ap-membership-eyebrow">Plan active</p>
            <h1>Your access is already live.</h1>
            <p>We verified your paid account, so you do not need to choose a plan again.</p>
            <div>
              <button type="button" onClick={() => navigate(returnPath || '/', { replace: true })}>Continue to study desk</button>
              <button type="button" onClick={() => navigate('/manage-subscription')}>Manage subscription</button>
            </div>
          </section>
        </div>
      </div>
    );
  }

  return (
    <div className="ap-membership-page">
      <div className="ap-membership-shell">
        <header className="ap-membership-header">
          <ApWordmark />
          {user ? <button type="button" onClick={handleSignOut}>Sign out</button> : <button type="button" onClick={() => navigate('/signin?redirect=%2Fmembership')}>Sign in</button>}
        </header>

        <section className="ap-membership-introduction">
          <p className="ap-membership-eyebrow">{isReturningCustomer ? 'Reactivate access' : 'Your 3-day trial'}</p>
          <div>
            <h1>{membershipCopy.title}</h1>
            <p>{membershipCopy.subtitle}</p>
          </div>
          {user ? <p className="ap-membership-account">Signed in as <strong>{user.email}</strong></p> : <p className="ap-membership-account">Choose a plan, then sign in securely with Google.</p>}
        </section>

        <section className="ap-membership-cycle-control" id="plans-anchor" aria-label="Billing frequency">
          <div>
            <button type="button" aria-label="Monthly" aria-pressed={billing === 'monthly'} data-active={billing === 'monthly' ? 'true' : 'false'} onClick={() => setBilling('monthly')}>Monthly</button>
            <button type="button" aria-label="Annual save 50%" aria-pressed={billing === 'annual'} data-active={billing === 'annual' ? 'true' : 'false'} onClick={() => setBilling('annual')}><span>Annual</span><small>50% off</small></button>
          </div>
          <p>{billing === 'annual' ? '$60 for a full year — half the usual $120 price.' : 'Choose a flexible month-to-month plan.'}</p>
        </section>

        {error ? <p className="ap-membership-error" role="alert">{error}</p> : null}

        <section className="ap-membership-plan-grid" data-single={plans.length === 1 ? 'true' : 'false'} aria-label="Subscription plans">
          {plans.map(plan => (
            <React.Fragment key={plan.id}>
              <PlanCard
                {...plan}
                onSelect={() => startCheckout(plan.id)}
                buttonLabel={primaryButtonLabel}
                buttonHint={user ? trialHint : guestHint}
                isLoading={loadingPlanId === plan.id}
                disabled={false}
                showFreeToday={showFreeToday}
              />
            </React.Fragment>
          ))}
        </section>

        <section className="ap-membership-benefits" aria-label="Membership benefits">
          {membershipCopy.bullets.map((bullet) => (
            <article key={bullet}><Check aria-hidden="true" /><p>{bullet}</p></article>
          ))}
        </section>

        <footer className="ap-membership-trust">
          <span>{isReturningCustomer ? 'Reactivate instantly' : '3-day free trial · cancel anytime'}</span>
          <span>Secure Stripe checkout</span>
        </footer>
      </div>
    </div>
  );
};

export default MembershipSelection;
